import React, { useMemo, useState } from 'react';
import PageHeader from './components/PageHeader';
import OrganizationsSelector from './OrganizationsSelector';
import { validateFields } from './utils/validation';
import DialogAlert from './components/DialogAlert';
import DialogFooter from './components/DialogFooter';
import { useAnimatedClose } from './hooks/useAnimatedClose';
import { ToastInline } from './components/ToastNotifications';

type EntityDialogProps = {
  title: React.ReactNode;
  contextLabel?: React.ReactNode;
  fields: any[];
  initialValues?: any;
  preview?: React.ReactNode | ((args: { form: any; fields: any[] }) => React.ReactNode);
  onClose: () => void;
  onSave: (payload: any) => void;
  testId?: string;
  saveDisabled?: boolean;
  topMessage?: string | null;
  saveHidden?: boolean;
};

export default function EntityDialog({
  title,
  contextLabel,
  fields,
  initialValues,
  preview,
  onClose,
  onSave,
  testId,
  saveDisabled = false,
  topMessage,
  saveHidden = false
}: EntityDialogProps) {
  const [form, setForm] = useState<Record<string, any>>({ ...(
      typeof initialValues === 'function'
      ? initialValues()
      : initialValues || {})
  });
  const [errors, setErrors] = useState<Record<string, any>>({});
  const { close, isClosing } = useAnimatedClose(onClose);

  const previewRows = useMemo(
    () => (fields || [])
      .filter((f) => !f.isHidden && !f.excludeFromPreview)
      .map((f) => {
        const key = f.name;
        const rawValue = form?.[key];
        let displayValue = rawValue;
        if (Array.isArray(rawValue)) displayValue = rawValue.join(', ');
        if (rawValue === undefined || rawValue === null || rawValue === '') displayValue = '—';
        if (f.type === 'checkbox') displayValue = rawValue ? 'Yes' : 'No';
        return { label: f.label || key, value: String(displayValue) };
      }),
    [fields, form]
  );

  const schema = useMemo(
    () => (fields || []).map((f) => ({
      name: f.name,
      required: f.required,
      validate: f.validate,
      message: f.message,
    })),
    [fields]
  );

  const update = (name, raw) => {
    setForm((prev) => {
      const field = (fields || []).find((f) => f.name === name);
      if (field && (field.type === 'number' || field.inputType === 'number')) {
        return { ...prev, [name]: raw === '' ? '' : Number(raw) };
      }
      return { ...prev, [name]: raw };
    });
  };

  const handleSave = () => {
    if (saveDisabled) return;

    const { errors: nextErrors, isValid } = validateFields(schema, form);
    setErrors(nextErrors);
    if (!isValid) return;

    const sanitized = { ...form };
    (fields || []).forEach((f) => {
      const value = form[f.name];
      if (f.type === 'number' || f.inputType === 'number') {
        sanitized[f.name] = Number(value) || 0;
      } else if (typeof value === 'string') {
        sanitized[f.name] = value.trim();
      } else {
        sanitized[f.name] = value;
      }
    });
    onSave(sanitized);
  };

  const renderField = (field) => {
    const isReadOnly = Boolean(field.readOnly);

    if (typeof field.render === 'function') {
      return field.render({
        form,
        setValue: update,
        errors
      });
    }

    const commonProps = {
      id: field.name,
      value: form[field.name] ?? '',
      onChange: (e) => update(field.name, e.target.value),
      className: `input ${errors[field.name] ? 'error' : ''}`,
      placeholder: field.placeholder || '',
      'aria-invalid': Boolean(errors[field.name]),
      readOnly: isReadOnly,
      disabled: isReadOnly,
      'data-testid': testId ? `${testId}-${field.name}` : undefined
    };

    if (field.type === 'org-selector' || field.type === 'facility-selector') {
      return (
        <OrganizationsSelector
          options={field.options || []}
          selected={Array.isArray(form[field.name]) ? form[field.name] : []}
          onChange={(next) => update(field.name, next)}
          error={errors[field.name]}
          label={field.label}
          required={field.required}
          defaultOpen={field.defaultOpen}
          testId={testId ? `${testId}-${field.name}` : undefined}
        />
      );
    }

    if (field.type === 'multiselect') {
      return (
        <select
          {...commonProps}
          multiple
          className={`select ${errors[field.name] ? 'error' : ''}`}
          value={Array.isArray(form[field.name]) ? form[field.name] : []}
          onChange={(e) => {
            const opts = Array.from(e.target.selectedOptions).map((o) => o.value);
            update(field.name, opts);
          }}
        >
          {(field.options || [])
            .filter((opt) => opt != null)
            .slice()
            .sort((a, b) => {
              const labelA = typeof a === 'string' ? a : a.label;
              const labelB = typeof b === 'string' ? b : b.label;
              return labelA.localeCompare(labelB, undefined, { sensitivity: 'base' });
            })
            .map((opt) => {
            const value = typeof opt === 'string' ? opt : opt.value;
            const label = typeof opt === 'string' ? opt : opt.label;
            return (
              <option key={value} value={value}>{label}</option>
            );
          })}
        </select>
      );
    }

    if (field.type === 'select') {
      return (
        <select {...commonProps} className={`select ${errors[field.name] ? 'error' : ''}`} disabled={isReadOnly}>
          {(field.options || [])
            .filter((opt) => opt != null)
            .slice()
            .sort((a, b) => {
              const labelA = typeof a === 'string' ? a : a.label;
              const labelB = typeof b === 'string' ? b : b.label;
              return labelA.localeCompare(labelB, undefined, { sensitivity: 'base' });
            })
            .map((opt) => {
              const value = typeof opt === 'string' ? opt : opt.value;
              const label = typeof opt === 'string' ? opt : opt.label;
              return (
                <option key={value} value={value}>{label}</option>
              );
            })}
        </select>
      );
    }

    if (field.type === 'textarea') {
      return <textarea {...commonProps} rows={field.rows || 3} className={`input ${errors[field.name] ? 'error' : ''}`} />;
    }

    const inputType = field.inputType || (field.type === 'number' ? 'number' : 'text');
    return <input {...commonProps} type={inputType} />;
  };

  return (
    <div className={`stack page-slide-in${isClosing ? ' page-slide-out' : ''}`} data-testid={testId || undefined}>
      <PageHeader
        title={title}
        subtitle={contextLabel}
        actions={(
          <button className="button secondary" onClick={close} data-testid={testId ? `${testId}-close` : undefined}>Back</button>
        )}
      />

      <div className={`drawer-body${preview ? '' : ''}`}>
        <div className="drawer-controls">
          <div className="drawer-controls-card">
            <DialogAlert show={Object.keys(errors).length > 0} />
            <ToastInline message={topMessage ?? null} />

            <div className="form-grid">
              {(fields || []).filter((field) => !field.isHidden).map((field) => {
                const isSelector = field.type === 'org-selector' || field.type === 'facility-selector';
                if (isSelector) {
                  return (
                    <div key={field.name} className="field full-span">
                      {renderField(field)}
                    </div>
                  );
                }
                return (
                  <div key={field.name} className="field full-span">
                    <label className="label" htmlFor={field.name}>
                      {field.label} {field.required && <span className="required-dot" aria-label="required">*</span>}
                    </label>
                    {renderField(field)}
                    {errors[field.name] && <div className="error-text">{errors[field.name]}</div>}
                  </div>
                );
              })}
            </div>

            <DialogFooter>
              <button className="button secondary" onClick={close} data-testid={testId ? `${testId}-cancel` : undefined}>Cancel</button>
              {!saveHidden && <button
                className="button"
                onClick={handleSave}
                disabled={saveDisabled}
                data-testid={testId ? `${testId}-save` : undefined}
              >
                Save
              </button>}
            </DialogFooter>
          </div>
        </div>

        <div className="drawer-preview">
          {preview
              ? (typeof preview === 'function' ? preview({ form, fields }) : preview)
              : (
                  <div className="card" style={{ padding: 16 }}>
                    <div className="muted" style={{ fontSize: 12, fontWeight: 800, marginBottom: 8 }}>Preview</div>
                    <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 10 }}>{title}</div>
                    <div style={{ display: 'grid', gap: 8 }}>
                      {previewRows.length === 0 && (
                          <div className="muted" style={{ fontSize: 12 }}>No fields to preview.</div>
                      )}
                      {previewRows.slice(0, 12).map((row) => (
                          <div key={row.label} style={{ display: 'grid', gap: 2 }}>
                            <div className="muted" style={{ fontSize: 11, fontWeight: 800 }}>{row.label}</div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)' }}>{row.value}</div>
                          </div>
                      ))}
                      {previewRows.length > 12 && (
                          <div className="muted" style={{ fontSize: 12 }}>+{previewRows.length - 12} more fields</div>
                      )}
                    </div>
                  </div>
              )}
        </div>
      </div>
    </div>
  );
}
